import { FolderOutlined, RightOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';
import React, { useState, useEffect } from 'react';
import { EntityType, GlossaryNode, GlossaryTerm } from '../../../types.generated';
import { useEntityRegistry } from '../../useEntityRegistry';
import { useGetGlossaryNodeQuery } from '../../../graphql/glossaryNode.generated';
import TermItem, { TermLink as NodeLink, NameWrapper } from './TermItem';
import { sortGlossaryNodes } from '../../entity/glossaryNode/utils';
import { sortGlossaryTerms } from '../../entity/glossaryTerm/utils';
import { useGlossaryEntityData } from '../../entity/shared/GlossaryEntityContext';
import { AutoTooltip } from '../../entity/shared/components/common/AutoTooltip';

const ItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    font-weight: 400;
`;

const NodeWrapper = styled.div`
    align-items: flex-start;
    display: flex;
    margin-bottom: 4px;
`;

const StyledRightOutlined = styled(RightOutlined)`
    cursor: pointer;
    margin-right: 6px;
    font-size: 14px;
    color: #8f9094;
    padding-top: 8px;
`;

const StyledDownOutlined = styled(DownOutlined)`
    cursor: pointer;
    margin-right: 6px;
    font-size: 14px;
    color: #8f9094;
    padding-top: 8px;
`;

const StyledFolderOutlined = styled(FolderOutlined)`
    color: #1ca8dd;
    margin-right: 8px;
    padding-top: 0px;
`;

const ChildrenWrapper = styled.div`
    border-left: solid 1px #e9e9ea;
    margin-left: 4px;
    padding-left: 12px;
    margin-bottom: 4px;
`;

const LoadingWrapper = styled.div`
    padding: 8px;
    display: flex;
    justify-content: center;

    svg {
        height: 15px;
        width: 15px;
    }
`;

interface Props {
    node: GlossaryNode;
    isSelecting?: boolean;
    hideTerms?: boolean;
    openToEntity?: boolean;
    refreshBrowser?: boolean;
    nodeUrnToHide?: string;
    selectTerm?: (urn: string, displayName: string) => void;
    selectNode?: (urn: string, displayName: string) => void;
}

function NodeItem(props: Props) {
    const { node, isSelecting, hideTerms, openToEntity, refreshBrowser, nodeUrnToHide, selectTerm, selectNode } = props;
    const shouldHideNode = nodeUrnToHide === node.urn;

    const [areChildrenVisible, setAreChildrenVisible] = useState(false);
    const entityRegistry = useEntityRegistry();
    const { entityData, urnsToUpdate, setUrnsToUpdate } = useGlossaryEntityData();
    const { data, loading, refetch } = useGetGlossaryNodeQuery({
        variables: { urn: node.urn },
        skip: !areChildrenVisible || shouldHideNode,
        errorPolicy: 'all',
    });

    useEffect(() => {
        if (openToEntity && entityData && entityData.parentNodes?.nodes.some((parent) => parent.urn === node.urn)) {
            setAreChildrenVisible(true);
        }
    }, [entityData, node.urn, openToEntity]);

    useEffect(() => {
        if (refreshBrowser) {
            setAreChildrenVisible(false);
        }
    }, [refreshBrowser]);

    useEffect(() => {
        if (urnsToUpdate.includes(node.urn)) {
            refetch();
            setUrnsToUpdate(urnsToUpdate.filter((urn) => urn !== node.urn));
        }
    });

    const isOnEntityPage = entityData && entityData.urn === node.urn;

    const children =
        entityData && isOnEntityPage ? entityData.children?.relationships : data?.glossaryNode?.children?.relationships;

    function handleSelectNode() {
        if (selectNode) {
            const displayName = entityRegistry.getDisplayName(node.type, node);
            selectNode(node.urn, displayName);
        }
    }

    const childNodes =
        (children as any)
            ?.filter((child) => child.entity?.type === EntityType.GlossaryNode)
            .sort((nodeA, nodeB) => sortGlossaryNodes(entityRegistry, nodeA.entity, nodeB.entity))
            .map((child) => child.entity) || [];
    const childTerms =
        (children as any)
            ?.filter((child) => child.entity?.type === EntityType.GlossaryTerm)
            .sort((termA, termB) => sortGlossaryTerms(entityRegistry, termA.entity, termB.entity))
            .map((child) => child.entity) || [];

    if (shouldHideNode) return null;

    return (
        <ItemWrapper aria-label={node?.properties?.name} tabIndex={0}>
            <NodeWrapper>
                {!areChildrenVisible && <StyledRightOutlined onClick={() => setAreChildrenVisible(true)} />}
                {areChildrenVisible && <StyledDownOutlined onClick={() => setAreChildrenVisible(false)} />}
                {!isSelecting && (
                    <NodeLink
                        to={`${entityRegistry.getEntityUrl(node.type, node.urn)}`}
                        isSelected={entityData?.urn === node.urn}
                        aria-label={node?.properties?.name}
                    >
                        <StyledFolderOutlined aria-hidden="true" />
                        <AutoTooltip>
                            {entityRegistry.getDisplayName(node.type, isOnEntityPage ? entityData : node)}
                        </AutoTooltip>
                    </NodeLink>
                )}
                {isSelecting && (
                    <NameWrapper showSelectStyles={!!selectNode} onClick={handleSelectNode}>
                        <StyledFolderOutlined aria-hidden="true" />
                        <span>{entityRegistry.getDisplayName(node.type, isOnEntityPage ? entityData : node)}</span>
                    </NameWrapper>
                )}
            </NodeWrapper>
            {areChildrenVisible && (
                <>
                    {!data && loading && (
                        <LoadingWrapper>
                            <LoadingOutlined />
                        </LoadingWrapper>
                    )}
                    {data && data.glossaryNode && (
                        <ChildrenWrapper>
                            {(childNodes as GlossaryNode[]).map((child) => (
                                <NodeItem
                                    node={child}
                                    isSelecting={isSelecting}
                                    hideTerms={hideTerms}
                                    openToEntity={openToEntity}
                                    nodeUrnToHide={nodeUrnToHide}
                                    selectTerm={selectTerm}
                                    selectNode={selectNode}
                                />
                            ))}
                            {!hideTerms &&
                                (childTerms as GlossaryTerm[]).map((child) => (
                                    <TermItem term={child} isSelecting={isSelecting} selectTerm={selectTerm} />
                                ))}
                        </ChildrenWrapper>
                    )}
                </>
            )}
        </ItemWrapper>
    );
}

export default NodeItem;
